import React, { useEffect, useState } from 'react';
import { observer } from 'mobx-react';
import { Checkbox, Form, Input, Select } from 'antd';
import { Rule } from 'antd/es/form';
import InputNumber from 'antd/lib/input-number';
import TextArea from 'antd/lib/input/TextArea';
import { useStepperContext } from './TariffCreationModal/context';
import { ISelectList } from '../../../interfaces';
import { useCompaniesSelect, useProfile } from '../../../hooks';
import { apiMessagesGetFormRules, getCompaniesSelect, rulesIsNumber } from '../../../middleware';
import { tF, tT, tV } from '../../../translate';
import TariffsStyles from '../m_styles.less';

export const BasicInfoStep: React.FC = observer(() => {
    const { companyId } = useProfile();
    const [form] = Form.useForm();
    const { formData, updateFormData } = useStepperContext();
    const [currentCompany, setCurrentCompany] = useState<string | number | undefined>(
        formData.companyId ? +formData.companyId : undefined,
    );
    const companies = useCompaniesSelect();
    const companySelect = companies.map((company: any) => ({
        label: company.name,
        value: +company.id,
        id: company.id,
    })) as never as ISelectList;

    const validatePrice: Array<Rule> = [
        rulesIsNumber,
        { required: true, message: tV('Please input price') },
        ...apiMessagesGetFormRules,
    ];

    useEffect(() => {
        if (companyId) {
            getCompaniesSelect(true);
        }
    }, [companyId]);

    useEffect(() => {
        form.setFieldsValue({
            name: formData.name,
            price: formData.price,
            price_cents: formData.priceCents,
            outOfBundle: formData.outOfBundle,
            description: formData.description,
            product_id: formData.productId,
            plan_type: formData.planType,
            company_id: formData.companyId || undefined,
        });
    }, [form, formData]);

    const handleValuesChange = (changedValues: any) => {
        const updates: any = {};

        if ('name' in changedValues) {
            updates.name = changedValues.name;
        }
        if ('price' in changedValues) {
            updates.price = Number(changedValues.price) || 0;
            updates.priceCents = Number(changedValues.price) * 100 || 0;
        }
        if ('outOfBundle' in changedValues) {
            updates.outOfBundle = changedValues.outOfBundle;
        }

        if ('description' in changedValues) {
            updates.description = changedValues.description;
        }
        if ('product_id' in changedValues) updates.productId = changedValues.product_id;
        if ('plan_type' in changedValues) updates.planType = changedValues.plan_type;

        if ('company_id' in changedValues) updates.companyId = changedValues.company_id;

        updateFormData(updates);
    };

    return (
        <div className={TariffsStyles.basicStep}>
            <Form
                layout={'vertical'}
                name="basic"
                className={'formStyle'}
                form={form}
                onValuesChange={handleValuesChange}
            >
                <div className={TariffsStyles.formRowItems}>
                    <Form.Item
                        label={tF('Tariff Name')}
                        name="name"
                        rules={[{ required: true, message: tV('Please input Name.') }]}
                        className={TariffsStyles.flex2}
                    >
                        <Input placeholder={tT('e.g. Uk Business Standard')} />
                    </Form.Item>
                    <Form.Item name="price" label={tF('Price')} rules={validatePrice} className={TariffsStyles.flex1}>
                        <InputNumber
                            step={1}
                            min={0.01}
                            precision={2}
                            addonBefore={'EUR'}
                            className={TariffsStyles.inputPrice}
                        />
                    </Form.Item>
                </div>
                <Form.Item
                    label={tF('Owner')}
                    name="company_id"
                    rules={[{ required: true, message: tV('Please select owner') }]}
                >
                    <Select
                        showSearch
                        filterOption={(input, option: any) =>
                            option?.label.toLowerCase().indexOf(input.toLowerCase()) >= 0 ||
                            option?.label.toLowerCase().indexOf(input.toLowerCase()) >= 0
                        }
                        placeholder={tF('Choose a company')}
                        className={TariffsStyles.selector}
                        options={companySelect}
                        value={currentCompany}
                        onChange={(value) => {
                            setCurrentCompany(value);
                            form.setFieldValue('company_id', value);
                            updateFormData({ tariffPackageIds: [] });
                        }}
                    />
                </Form.Item>
                <Form.Item label={tF('Description')} name="description">
                    <TextArea
                        autoSize={{ minRows: 3, maxRows: 6 }}
                        placeholder={tT('Describe the tariff plan in detail.')}
                    />
                </Form.Item>
                <Form.Item name="outOfBundle" valuePropName="checked" rules={apiMessagesGetFormRules}>
                    <Checkbox>{tF('Out of Bundle')}</Checkbox>
                </Form.Item>
                <div className={TariffsStyles.formRowItems}>
                    <Form.Item label={tF('Product ID')} name="product_id" className={TariffsStyles.flex1}>
                        <Input placeholder={tT('e.g. prod_123')} />
                    </Form.Item>
                    <Form.Item label={tF('Plan Type')} name="plan_type" className={TariffsStyles.flex1}>
                        <Input placeholder={tT('e.g. monthly')} />
                    </Form.Item>
                </div>
            </Form>
        </div>
    );
});
